import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { translate, type TranslationKey } from './dictionary'
import { pluralForm } from '@/lib/utils'
import {
  getStoredLocale,
  initLocale,
  setStoredLocale,
  syncDocumentLanguage,
  type Locale,
} from './locale'

interface I18nState {
  locale: Locale
  /** Whether this is a deliberate choice or just what the server last said. */
  isChosen: boolean
  /**
   * Switch language, everywhere it needs to reach.
   *
   * Deliberately async and deliberately awaited by the switcher: for a signed-in
   * user the server has to be told, because the server owns half the strings on
   * screen and all of the ones in e-mail.
   */
  setLocale: (locale: Locale, options?: { persistToAccount?: boolean }) => Promise<void>
  t: (key: TranslationKey, values?: Record<string, string | number>) => string
  /**
   * A count and its noun, in the form the number requires.
   *
   * Takes the key *stem* and appends `.one` / `.few` / `.many` — so
   * `tp('queue.pending', 3)` reads `queue.pending.few`. All three have to exist
   * in the Bosnian file, which is what makes the stem checkable at compile
   * time; passing a stem with no forms behind it will not type.
   *
   * `{count}` is substituted for free, since a plural without its number is
   * never what the caller wanted.
   */
  tp: (
    stem: PluralStem,
    count: number,
    values?: Record<string, string | number>,
  ) => string
}

/**
 * Key stems that have all three plural forms.
 *
 * Derived from the dictionary rather than declared, so a stem is only offered
 * once somebody has actually written `.one`, `.few` and `.many` for it —
 * `tp('queue.pending', n)` compiles and `tp('nav.home', n)` does not.
 */
type StemWithAllForms<K extends string> = K extends `${infer Stem}.one`
  ? // The tuple is what makes both checks happen in one conditional; written as
    // two nested ones, the inner `extends` stops distributing and every stem
    // collapses to `never`.
    [`${Stem}.few`, `${Stem}.many`] extends [TranslationKey, TranslationKey]
    ? Stem
    : never
  : never

type PluralStem = StemWithAllForms<TranslationKey>

const I18nContext = createContext<I18nState | null>(null)

/**
 * The language the interface is in.
 *
 * ## Why this is not a purely client-side concern
 *
 * Every enum label in this application — statuses, types, priorities, roles,
 * months — is rendered from strings the **server** sends, because in the legacy
 * app they lived in App.tsx as well and the two copies drifted until a manager
 * saw `IN_PROGRESS` on a badge. So switching language here would repaint the
 * buttons and leave every badge, filter and dropdown in the old one.
 *
 * Three things therefore happen on a switch, and all three are needed:
 *
 * 1. This context re-renders the strings the client owns.
 * 2. `Accept-Language` changes, so the next response is labelled correctly.
 * 3. The React Query cache is cleared of anything holding server text.
 *
 * ## Why no i18n library
 *
 * react-i18next and friends bring plural rules, interpolation, namespaces,
 * lazy-loaded bundles and a detection chain. Of those this application needs
 * two, and both are already here: interpolation is the regex in
 * `dictionary.ts`, and the Slavic three-form plural is `tp()` above, resting on
 * a rule `lib/utils.ts` had implemented for its own reasons long before any of
 * this existed.
 *
 * The rest is unused or actively wrong here. Detection would read
 * `navigator.language` and put a Bosnian floor into English; lazy bundles would
 * add a network round trip to an app whose whole point is working without one.
 * That is roughly 40 KB of dependency to replace about forty lines, and the
 * forty lines are the ones anybody debugging a missing label would want to
 * read.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // `initLocale` reads storage once and seeds the module-level value the axios
  // interceptor reads, so the very first request already carries the header.
  const [locale, setLocaleState] = useState<Locale>(() => initLocale())
  const [isChosen, setIsChosen] = useState<boolean>(() => getStoredLocale() !== null)

  const setLocale = useCallback(
    async (next: Locale, options?: { persistToAccount?: boolean }) => {
      /*
       * Stored before the request, not after.
       *
       * `setStoredLocale` is what the axios interceptor reads, so doing it
       * first is what makes the PATCH below carry the new language and come
       * back with its confirmation message already translated. It also means a
       * failed request still leaves the client switched, which is the right
       * way round: the person asked for a language, and refusing to change what
       * we control because the network is down helps nobody.
       */
      setStoredLocale(next)
      setLocaleState(next)
      setIsChosen(true)

      if (options?.persistToAccount) {
        /*
         * So the choice outlives this device.
         *
         * It is what puts the 06:00 overdue digest in the right language — that
         * mail is composed by a scheduled command with no request behind it, so
         * a header cannot reach it — and what carries the choice to the shared
         * terminal by the door.
         *
         * Swallowed on failure. The switch has already happened locally and
         * telling somebody their language did not save is a worse outcome than
         * quietly saving it next time they change it.
         */
        await api.patch('/profile', { locale: next }).catch(() => {})
      }

      /*
       * Every cached response is in the old language.
       *
       * `/api/meta` is the obvious one — it is the source of every badge and
       * dropdown label — but it is not the only one: submission resources carry
       * `statusLabel`, users carry `roleLabel`, and analytics carry month names.
       * Anything short of clearing the cache leaves some screen mixed, so this
       * refetches rather than trying to enumerate what was affected.
       */
      await queryClient.invalidateQueries()
    },
    [queryClient],
  )

  /*
   * Keyed off the rendered locale, not the stored one, so the attribute always
   * describes what is actually on screen — including the first paint, where
   * `initLocale` may have picked up a stored choice that differs from the `bs`
   * baked into index.html.
   */
  useEffect(() => {
    syncDocumentLanguage(locale)
  }, [locale])

  const value = useMemo<I18nState>(
    () => ({
      locale,
      isChosen,
      setLocale,
      t: (key, values) => translate(locale, key, values),
      tp: (stem, count, values) =>
        translate(
          locale,
          `${stem}.${pluralForm(count, locale)}` as TranslationKey,
          { count, ...values },
        ),
    }),
    [locale, isChosen, setLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nState {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>')
  return context
}

/**
 * Just the translate function, for the common case.
 *
 * `const t = useT()` reads better at every call site than destructuring the
 * whole context, and it is what almost every component wants.
 */
export function useT(): I18nState['t'] {
  return useI18n().t
}
